import { useEffect, useMemo, useState } from "react";
import { patientService } from "../../services/patient.services";
import { helpFunc } from "../../helpers/helpFunction";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { validation } from "../../helpers/validation_schemes/patientValidation";

export const AnamnezaLogic = (patient, getPatientCallback) => {
  const [weightHistory, setWeightHistory] = useState([]);
  const [options, setOptions] = useState([]);

  // Local storage helper functions
  const getLocalStorageKey = () => `anamneza_patient_${patient.id}`;

  const saveToLocalStorage = (data) => {
    try {
      localStorage.setItem(getLocalStorageKey(), JSON.stringify(data));
    } catch (error) {
      console.error("Error saving to localStorage:", error);
    }
  };

  const loadFromLocalStorage = () => {
    try {
      const savedData = localStorage.getItem(getLocalStorageKey());
      return savedData ? JSON.parse(savedData) : null;
    } catch (error) {
      console.error("Error loading from localStorage:", error);
      return null;
    }
  };

  const clearLocalStorage = () => {
    try {
      localStorage.removeItem(getLocalStorageKey());
    } catch (error) {
      console.error("Error clearing localStorage:", error);
    }
  };

  useEffect(() => {
    getWeightHistory();
    getOptions();
    setFormValues(patient);
  }, []);

  const getWeightHistory = async () => {
    const response = await patientService.getWeightHistory(patient.id);
    if (response.status === 200) {
      const dataWithFormattedDate = response.data.map((item) => ({
        ...item,
        date: helpFunc.formatDateWithTime(item.history_datetime),
      }));

      // Sort by history_datetime in descending order (newest first)
      const sortedData = dataWithFormattedDate.sort(
        (a, b) => new Date(b.history_datetime) - new Date(a.history_datetime)
      );

      const processedData = sortedData.map((item, index, array) => {
        if (index === 0) return item;

        const prevItem = array[index - 1];
        return {
          ...item,
          weight: item.weight === prevItem.weight ? "---" : item.weight,
          height: item.height === prevItem.height ? "---" : item.height,
        };
      });

      setWeightHistory(processedData);
    }
  };

  const handleSavePatient = async (data) => {
    const response = await patientService.patchPatient(patient.id, {
      ...data,
      body_constitution: data.body_constitution.value || null,
      allergy_drugs: data.allergy_drugs
        .filter((item) => item.value !== "")
        .map((item) => item.value),
      chronic_medication_drugs: data.chronic_medication_drugs
        .filter((item) => item.value !== "")
        .map((item) => item.value),
    });
    if (response.status === 200) {
      // Clear localStorage after successful save
      clearLocalStorage();
      getPatientCallback();
    }
  };

  const getOptions = async () => {
    const response = await patientService.getBodyConstitutions();
    if (response.status === 200) {
      setOptions(
        response.data.results.map((item) => ({
          label: item.name,
          value: item.id,
        }))
      );
    }
  };

  const columns = useMemo(() => [
    { Header: "Datum", accessor: "date" },
    { Header: "Váha (kg)", accessor: "weight" },
    { Header: "Výška (cm)", accessor: "height" },
  ]);

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      allergy_drugs: [{ label: "Vyhledejte...", value: "" }],
      chronic_medication_drugs: [{ label: "Vyhledejte...", value: "" }],
      body_constitution: { label: "Vyhledejte...", value: "" },
    },
  });

  // Watch form changes and save to localStorage with debounce
  useEffect(() => {
    const subscription = methods.watch((value) => {
      // Debounce the save operation
      const timeoutId = setTimeout(() => {
        saveToLocalStorage(value);
      }, 500);

      return () => clearTimeout(timeoutId);
    });

    return () => subscription.unsubscribe();
  }, [methods.watch]);

  const setFormValues = async (data) => {
    // Check if there are saved data in localStorage first
    const savedData = loadFromLocalStorage();
    const dataToUse = savedData || data;

    // If using saved data, use it directly, otherwise process the original data
    if (savedData) {
      // Set values directly from localStorage
      Object.keys(savedData).forEach((key) => {
        if (savedData[key] !== undefined && savedData[key] !== null) {
          methods.setValue(key, savedData[key]);
        }
      });
    } else {
      // Use original data processing logic
      methods.setValue("weight", data.weight);
      methods.setValue("height", data.height);
      methods.setValue("correction_factor", data.correction_factor);
      methods.setValue("allergy_note", data.allergy_note);
      methods.setValue("chronic_medication_note", data.chronic_medication_note);
      methods.setValue("body_constitution_note", data.body_constitution_note);

      if (data.body_constitution) {
        methods.setValue("body_constitution", {
          value: data.body_constitution?.id,
          label: data.body_constitution?.name,
        });
      } else {
        methods.setValue("body_constitution", {
          label: "Vyhledejte...",
          value: "",
        });
      }

      methods.setValue(
        "allergy_drugs",
        helpFunc.parseDataForDrugsSelectNoSync(data.allergy_drugs)
      );
      methods.setValue(
        "chronic_medication_drugs",
        helpFunc.parseDataForDrugsSelectNoSync(data.chronic_medication_drugs)
      );
    }
  };

  return { weightHistory, columns, methods, options, handleSavePatient };
};

const schema = yup.object().shape({
  weight: yup.number(validation.number).required(validation.required),
  height: yup.number(validation.number).required(validation.required),
});
